// ============================================================
// Offline queue for OnSiteComms
// Persists unsent messages/photos in localStorage when the job
// site has weak/no signal, and auto-flushes on reconnect.
// ============================================================
import { api, uploadMedia, dataUrlToBase64 } from "../api";

const KEY = "osc_offline_queue_v1";
const listeners = new Set();
let flushing = false;
let intervalId = null;

function genId() {
  try {
    return crypto.randomUUID();
  } catch (e) {
    return `cq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    return [];
  }
}

function writeQueue(q) {
  localStorage.setItem(KEY, JSON.stringify(q));
  notify();
}

function notify() {
  const q = readQueue();
  listeners.forEach((fn) => {
    try { fn(q); } catch (e) { /* ignore */ }
  });
}

function updateItem(clientId, patch) {
  const q = readQueue();
  const idx = q.findIndex((i) => i.client_id === clientId);
  if (idx >= 0) {
    q[idx] = { ...q[idx], ...patch };
    try { writeQueue(q); } catch (e) { /* ignore quota */ }
  }
}

// ---- Public API ----

/** Subscribe to queue changes. Returns unsubscribe fn. Immediately calls back with current queue. */
export function subscribeQueue(fn) {
  listeners.add(fn);
  try { fn(readQueue()); } catch (e) { /* ignore */ }
  return () => listeners.delete(fn);
}

/** Get queued items (optionally for a single conversation). */
export function getQueued(conversationId = null) {
  const q = readQueue();
  return conversationId ? q.filter((i) => i.conversation_id === conversationId) : q;
}

/**
 * Enqueue a message for later delivery.
 * item: { kind: 'text'|'photo', conversation_id, sender_id, payload, preview? }
 * Returns the entry or null if storage is full.
 */
export function enqueueMessage(item) {
  const entry = {
    client_id: genId(),
    attempts: 0,
    status: "pending", // pending | sending | failed
    created_at: new Date().toISOString(),
    ...item,
  };
  const q = readQueue();
  q.push(entry);
  try {
    writeQueue(q);
  } catch (e) {
    // localStorage quota exceeded (large photos)
    return null;
  }
  ensureAutoFlush();
  return entry;
}

export function removeQueued(clientId) {
  writeQueue(readQueue().filter((i) => i.client_id !== clientId));
}

export function retryQueued(clientId) {
  updateItem(clientId, { status: "pending" });
  flushQueue();
}

// ---- Delivery ----

async function sendItem(item) {
  if (item.kind === "text") {
    const res = await api.post("/messages", { ...item.payload, client_id: item.client_id });
    return res.data;
  }
  if (item.kind === "photo") {
    const p = item.payload;
    // Upload original once (id persisted back into the queue to avoid re-upload on retry)
    let originalId = item.uploaded_original_id;
    if (!originalId) {
      const original = await uploadMedia({
        base64: dataUrlToBase64(p.originalDataUrl),
        jobId: p.jobId || null,
        category: p.category || null,
        uploadedBy: item.sender_id,
      });
      originalId = original.id;
      updateItem(item.client_id, { uploaded_original_id: originalId });
    }
    let finalId = originalId;
    if (p.editedDataUrl) {
      let editedId = item.uploaded_edited_id;
      if (!editedId) {
        const edited = await uploadMedia({
          base64: dataUrlToBase64(p.editedDataUrl),
          jobId: p.jobId || null,
          category: p.category || null,
          parentId: originalId,
          versionLabel: p.versionLabel || "Markup",
          uploadedBy: item.sender_id,
          textOverlays: p.textOverlays || null,
        });
        editedId = edited.id;
        updateItem(item.client_id, { uploaded_edited_id: editedId });
      }
      finalId = editedId;
    }
    const res = await api.post("/messages", {
      conversation_id: item.conversation_id,
      sender_id: item.sender_id,
      type: "photo",
      content: p.caption || "",
      media_ids: [finalId],
      meta: p.meta || {},
      reply_to: p.reply_to || null,
      client_id: item.client_id,
    });
    return res.data;
  }
  return null;
}

/** Try to deliver all queued items in order. Stops early if the network is down. */
export async function flushQueue() {
  if (flushing) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const snapshot = readQueue();
  if (snapshot.length === 0) return;
  flushing = true;
  try {
    for (const it of snapshot) {
      // Re-read latest version (upload ids may have been persisted)
      const current = readQueue().find((i) => i.client_id === it.client_id);
      if (!current || current.status === "sending") continue;
      updateItem(current.client_id, { status: "sending" });
      try {
        const sent = await sendItem(current);
        removeQueued(current.client_id);
        // Let open threads render the delivered message immediately
        // (the WebSocket may still be reconnecting at this point)
        if (sent && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("osc-queued-sent", { detail: sent }));
        }
      } catch (e) {
        const isNetworkError = !e?.response;
        updateItem(current.client_id, {
          attempts: (current.attempts || 0) + 1,
          status: isNetworkError ? "pending" : "failed",
        });
        if (isNetworkError) break; // connection still down — stop and wait
      }
    }
  } finally {
    flushing = false;
    notify();
  }
}

/** Keep a light interval running while there are queued items. */
export function ensureAutoFlush() {
  if (intervalId) return;
  intervalId = setInterval(() => {
    if (readQueue().length === 0) {
      clearInterval(intervalId);
      intervalId = null;
      return;
    }
    flushQueue();
  }, 12000);
}

// ---- Global triggers (module scope, runs once) ----
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    ensureAutoFlush();
    flushQueue();
  });
  // Flush anything left over from a previous session shortly after app start
  setTimeout(() => {
    if (readQueue().length > 0) {
      ensureAutoFlush();
      flushQueue();
    }
  }, 2500);
}
